import { useEffect, useRef, useState } from "react";

/**
 * FlyWords — staggered word reveal animation.
 * Wraps each word in a <span class="fly-word"> that translates from below
 * with a slight blur + scale, triggered when the heading scrolls into view.
 *
 * Usage:
 *   <FlyWords
 *     as="h2"
 *     className="font-display text-5xl"
 *     segments={[
 *       { text: "A wall should make" },
 *       { type: "break" },
 *       { text: "a statement.", className: "italic text-[#D4AF37]" },
 *     ]}
 *   />
 */
export default function FlyWords({
  segments = [],
  as: Tag = "h2",
  className = "",
  style,
  baseDelay = 0,
  wordDelay = 65,
  threshold = 0.15,
  dataTestid,
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [threshold]);

  let wIndex = 0;
  return (
    <Tag
      ref={ref}
      className={`fly-words ${className}`}
      style={style}
      data-testid={dataTestid}
      data-visible={visible || undefined}
    >
      {segments.map((seg, si) => {
        if (seg.type === "break") return <br key={`b-${si}`} />;
        const words = seg.text.split(" ");
        return (
          <span key={`s-${si}`} className={seg.className || ""}>
            {words.map((w, i) => {
              const idx = wIndex++;
              return (
                <span
                  key={`w-${si}-${i}`}
                  className="fly-word"
                  style={{
                    transitionDelay: `${baseDelay + idx * wordDelay}ms`,
                  }}
                >
                  {w}
                  {i < words.length - 1 ? "\u00A0" : ""}
                </span>
              );
            })}
          </span>
        );
      })}
    </Tag>
  );
}
